import { defineComponent, h } from 'vue'
import { VTextField } from 'vuetify/components'
import { parseNumericFieldValue } from '~/cynqueModules/dataObjects/Numeric'

const mn = '[components/BaseNumericVTextField.js]'

export function enforceFormat(event, amount, maximumFractionDigits) {
  const keyCode = event.keyCode ? event.keyCode : event.which
  const numAsString = amount ? amount.toString() : null

  const illegalKeyCode = (keyCode, isSelectedEntireStr) => {
    const DOT = 46
    console.debug(numAsString)
    return (
      (keyCode < 48 || keyCode > 57) &&
      (keyCode !== DOT ||
        (numAsString && numAsString.includes('.') && !isSelectedEntireStr))
    )
  }

  const moreThanXDecimalPlaces = (numAsString, isSelectedEntireStr) => {
    return (
      numAsString != null &&
      numAsString.includes('.') &&
      numAsString.split('.')[1].length > maximumFractionDigits - 1 &&
      !isSelectedEntireStr
    )
  }

  const isSelectedEntireString = (numAsString, event) => {
    const fn = `${mn} isSelectedEntireString |`
    if (!numAsString) {
      console.debug(`${fn} numAsString is falsey. Returning false`)
      return false
    }
    const start = event.srcElement.selectionStart
    const end = event.srcElement.selectionEnd
    const selectedLength = end - start
    const numAsStringLength = numAsString.length
    const diff = numAsStringLength - selectedLength
    console.debug(
      `${fn} start: ${start}, end: ${end}, selectedLength: ${selectedLength},` +
        ` numAsStringLength: ${numAsStringLength}, !diff: ${!diff}`
    )
    const isSelectedEntireStr = selectedLength > 0 && !diff
    console.debug(`${fn} returning:`, isSelectedEntireStr)
    return isSelectedEntireStr
  }
  //
  // execution starts here
  //
  if (
    illegalKeyCode(keyCode, isSelectedEntireString(numAsString, event)) ||
    moreThanXDecimalPlaces(
      numAsString,
      isSelectedEntireString(numAsString, event)
    )
  ) {
    event.preventDefault()
  }
}

const defaultNumberFormatter = new Intl.NumberFormat('en-US', {})

// Vuetify 3 migration: this was `extends: VTextField` (Vuetify 2), reaching into the
// component's internal `lazyValue` / `isFocused` to show a raw value while focused and a
// formatted value once blurred. Vuetify 3's VTextField is a setup()-based component whose
// internals aren't exposed on `this`, so we WRAP `<v-text-field>` instead and manage the
// raw-vs-formatted display ourselves.
//
// Model contract (numeric): `modelValue` stays RAW (the typed string / API number) — the
// display is what gets formatted. getDirtyFieldsObject converts the raw value to a number
// at save time (see cynqueModules/componentHelpers/entityMaskForm/dirtyFields.ts). This
// mirrors Vuetify 2, where assigning `lazyValue = formatted` changed only the display and
// never emitted. (Contrast PhoneNumberVTextField, which commits the FORMATTED value, and
// PercentageVTextField, which commits a fraction — both override the hooks below.)
export default defineComponent({
  name: 'BaseNumberVTextField',
  // We forward attrs (label, rules, class, disabled, @keypress, @input, ...) onto the inner
  // field ourselves so the wrapper's own root doesn't also receive them.
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    blurredFormat: {
      type: Function,
      default: (v) => {
        if (v) return defaultNumberFormatter.format(parseFloat(v))
      },
    },
    // Field type used to decide what counts as a clean number for this input
    // (see cynqueModules/dataObjects/Numeric). Overridden by the currency/percentage
    // subclasses; defaults to the plain numeric type.
    numericType: {
      type: String,
      default: 'numberTextField',
    },
    // The numeric subclasses parse a number out of the input; the date/time/phone
    // subclasses set this false so formatForDisplay formats the raw value directly
    // (there is no number to extract).
    parseNumeric: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'clearModelValue'],
  data() {
    return {
      // What the inner text field shows: formatted when blurred, raw when focused.
      display: '',
      isFocused: false,
    }
  },
  computed: {
    // `readonly` is not a declared prop, so it reaches us through $attrs. A bare
    // `readonly` attribute in a template compiles to the empty string; an explicit
    // binding (`:readonly="isViewMode || ..."`) gives a real boolean.
    isReadonly() {
      const readonly = this.$attrs.readonly
      return readonly === '' || readonly === true || readonly === 'readonly'
    },
  },
  watch: {
    // An external model change (the parent set it) while we are not focused re-derives the
    // formatted display. While focused we leave the field alone so typing isn't disturbed
    // — except when readonly, which has no in-progress edit to protect. See
    // onInnerFocusChange for why that exception exists.
    modelValue(v) {
      if (!this.isFocused || this.isReadonly) {
        this.display = this.formatForDisplay(v)
      }
    },
  },
  mounted() {
    this.display = this.formatForDisplay(this.modelValue)
  },
  methods: {
    // Map a raw model value to its blurred (formatted) display. Genuinely-invalid input
    // (e.g. a pasted '1-50 employees') is shown verbatim so it sits next to its validation
    // error instead of being silently coerced.
    formatForDisplay(raw) {
      if (raw === '' || raw === null || raw === undefined) {
        return ''
      }
      // Non-numeric formatted fields (date/time/phone) format the raw value directly.
      if (!this.parseNumeric) {
        return this.blurredFormat(raw) ?? ''
      }
      const parsed = parseNumericFieldValue(raw, this.numericType)
      if (parsed === null) {
        return String(raw)
      }
      return this.blurredFormat(parsed) ?? ''
    },
    // The editable (raw) display shown when the field gains focus. Percentage overrides this
    // to expand its fractional model back to a whole number.
    rawForEditing() {
      return this.modelValue === null || this.modelValue === undefined
        ? ''
        : String(this.modelValue)
    },
    // User typing. The numeric model tracks the raw typed string; the '.'-only guard emits
    // the bespoke clearModelValue event so the parent can null the field.
    onInnerInput(v) {
      this.display = v ?? ''
      this.$emit('update:modelValue', this.display)
    },
    // A readonly field cannot be typed into, so there is no in-progress edit here: its
    // value is driven entirely from outside — the dateField / timeField pickers use these
    // components as their v-menu ACTIVATOR and write straight to the model. Such a field
    // must therefore never swap in the raw editing value and never run the blur reformat;
    // its display just tracks the model.
    //
    // Vuetify 2 got this for free: it never focused the readonly activator, so `isFocused`
    // stayed false and the modelValue watcher above always ran. Vuetify 3 DOES focus it,
    // which froze the display at the pre-pick value while the picker menu held focus, and
    // then commitBlur cemented that stale string — so picking 3:25 left the field reading
    // 11:26 AM while 03:25 was what got saved.
    onInnerFocusChange(focused) {
      const wasFocused = this.isFocused
      this.isFocused = focused
      if (this.isReadonly) {
        this.display = this.formatForDisplay(this.modelValue)
        return
      }
      if (focused && !wasFocused) {
        this.display = this.rawForEditing()
      } else if (!focused && wasFocused) {
        this.commitBlur()
      }
    },
    // On blur: reformat the display and re-validate so the error state matches exactly what
    // the user is left looking at. The raw model was already emitted while typing, so the
    // numeric base does not re-emit here (subclasses that need to override the committed
    // value — percentage, phone — do so by overriding this method).
    commitBlur() {
      if (this.display === '.') {
        this.$emit('clearModelValue')
      }
      this.display = this.formatForDisplay(this.display)
      this.$nextTick(() => this.$refs.field?.validate?.())
    },
    focus() {
      this.$el?.querySelector?.('input')?.focus()
    },
    blur() {
      this.$el?.querySelector?.('input')?.blur()
    },
  },
  render() {
    return h(VTextField, {
      ref: 'field',
      ...this.$attrs,
      modelValue: this.display,
      'onUpdate:modelValue': this.onInnerInput,
      'onUpdate:focused': this.onInnerFocusChange,
    })
  },
})
